import { VariantProps, cva } from 'class-variance-authority'
import { cn } from 'lib/utils'
import * as React from 'react'

/**
 * T3 typographic primitive for titles. Routes through the locked product
 * type scale (.t-*) defined in styles/design-system.css.
 *
 * Spec: playbook/design-system/foundations.md#product-type-scale.
 *
 * - level="display"  → marketing-grade hero title (rare, 1 per page max)
 * - level="h1"       → page title
 * - level="h2"       → section title
 * - level="h3"       → subsection / card title
 *
 * Legacy variant prop (h1..h6) maps onto the new ladder so existing call
 * sites keep working through the migration window. After the migration
 * sweep, prefer `level` and drop `variant`.
 */
const headingVariants = cva('', {
  variants: {
    level: {
      display: 't-display',
      h1: 't-h1',
      h2: 't-h2',
      h3: 't-h3',
    },
    // Legacy `variant` prop folds the old 6-step ladder into the new
    // 4-step locked scale. h2/h3 → t-h1 and h4/h5 → t-h2 is intentional
    // down-resolution: the new scale spreads h1↔h3 wider, so legacy
    // headings that used to be "almost h1" or "section-ish h3" all read
    // best at the next-bigger locked step. Keeps the migration window
    // visually correct without per-callsite remapping.
    variant: {
      h1: 't-h1',
      h2: 't-h1',
      h3: 't-h2',
      h4: 't-h2',
      h5: 't-h3',
      h6: 't-h3',
    },
  },
})

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

export interface HeadingProps
  extends React.HTMLAttributes<HTMLHeadingElement>, VariantProps<typeof headingVariants> {
  asChild?: boolean
  as?: HeadingTag
}

const LEVEL_TO_TAG: Record<string, HeadingTag> = {
  display: 'h1',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
}

const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ className, level, variant, as, children, ...props }, ref) => {
    const resolvedLevel = level ?? (variant ? undefined : 'h1')
    const Tag: HeadingTag =
      as ?? (resolvedLevel ? LEVEL_TO_TAG[resolvedLevel] : (variant as HeadingTag)) ?? 'h1'

    return (
      <Tag
        ref={ref}
        className={cn(headingVariants({ level: resolvedLevel, variant }), className)}
        data-slot="heading"
        {...props}
      >
        {children}
      </Tag>
    )
  }
)

Heading.displayName = 'Heading'

export { Heading, headingVariants }
